import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Box, ImageIcon, Sun } from 'lucide-react';

export interface MediaItem {
  id: string;
  url: string;
  type: 'image' | 'model3d';
  order_index: number;
}

interface FigurineGalleryProps {
  items: MediaItem[];
  title: string;
  accentColor?: string;
}

function ModelViewerSlide({ url, accentColor }: { url: string; accentColor: string }) {
  const [exposure, setExposure] = useState(1.0);

  return (
    <div className="w-full h-full relative flex items-center justify-center"
      style={{ background: `radial-gradient(ellipse 80% 70% at 50% 60%, ${accentColor}15 0%, oklch(0.10 0.022 280) 70%)` }}>

      {/* Grid background */}
      <div className="absolute inset-0 pointer-events-none" style={{
        backgroundImage: `linear-gradient(oklch(0.25 0.045 280 / 0.25) 1px, transparent 1px), linear-gradient(90deg, oklch(0.25 0.045 280 / 0.25) 1px, transparent 1px)`,
        backgroundSize: '24px 24px',
      }} />

      {/* @ts-expect-error model-viewer is a custom element loaded via CDN */}
      <model-viewer
        src={url}
        auto-rotate
        auto-rotate-delay="0"
        rotation-per-second="30deg"
        camera-controls
        shadow-intensity="1"
        environment-image="neutral"
        exposure={exposure}
        style={{ width: '100%', height: '100%', background: 'transparent' }}
      />

      {/* 3D badge */}
      <div className="absolute top-3 left-3 flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold z-10"
        style={{ background: `${accentColor}25`, color: accentColor, border: `1px solid ${accentColor}50`, fontFamily: 'JetBrains Mono, monospace' }}>
        <Box className="w-3 h-3" />
        MODEL 3D
      </div>

      {/* ── Brightness slider ── */}
      <div
        className="absolute bottom-10 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2.5 px-3.5 py-2 rounded-full"
        style={{
          background: 'oklch(0.10 0.022 280 / 0.82)',
          border: `1px solid ${accentColor}45`,
          backdropFilter: 'blur(10px)',
          boxShadow: `0 0 16px ${accentColor}25`,
          width: 'min(220px, 70%)',
        }}
        /* prevent model-viewer from intercepting pointer events on the slider panel */
        onPointerDown={e => e.stopPropagation()}
      >
        {/* dim icon */}
        <Sun className="w-3 h-3 flex-shrink-0 opacity-40" style={{ color: accentColor }} />

        {/* slider */}
        <input
          type="range"
          min={0.2}
          max={3.0}
          step={0.05}
          value={exposure}
          onChange={e => setExposure(Number(e.target.value))}
          className="flex-1 h-1.5 appearance-none rounded-full outline-none cursor-pointer"
          style={{
            // track
            background: `linear-gradient(to right, ${accentColor} 0%, ${accentColor} ${((exposure - 0.2) / (3.0 - 0.2)) * 100}%, oklch(0.28 0.045 280) ${((exposure - 0.2) / (3.0 - 0.2)) * 100}%, oklch(0.28 0.045 280) 100%)`,
            // webkit thumb handled via global css fallback — inline style covers the rest
            accentColor: accentColor,
          } as React.CSSProperties}
        />

        {/* bright icon */}
        <Sun className="w-4 h-4 flex-shrink-0" style={{ color: accentColor }} />

        {/* numeric readout */}
        <span
          className="text-xs font-bold flex-shrink-0 w-8 text-right"
          style={{ fontFamily: 'JetBrains Mono, monospace', color: accentColor }}
        >
          {exposure.toFixed(1)}
        </span>
      </div>
    </div>
  );
}

export default function FigurineGallery({ items, title, accentColor = 'var(--primary)' }: FigurineGalleryProps) {
  const [current, setCurrent] = useState(0);
  const sorted = [...items].sort((a, b) => a.order_index - b.order_index);

  if (sorted.length === 0) return null;

  const prev = () => setCurrent(i => (i - 1 + sorted.length) % sorted.length);
  const next = () => setCurrent(i => (i + 1) % sorted.length);
  const item = sorted[current];

  return (
    <div className="w-full flex flex-col gap-2">
      {/* Main viewer – strictly 9:16 */}
      <div className="relative w-full overflow-hidden rounded-xl border"
        style={{
          aspectRatio: '9/16',
          borderColor: 'oklch(0.25 0.045 280)',
          background: 'oklch(0.13 0.028 280)',
        }}>
        <AnimatePresence mode="wait">
          <motion.div
            key={`${item.id}-${current}`}
            initial={{ opacity: 0, scale: 1.03 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.97 }}
            transition={{ duration: 0.25 }}
            className="absolute inset-0"
          >
            {item.type === 'model3d' ? (
              <ModelViewerSlide url={item.url} accentColor={accentColor} />
            ) : (
              <div className="w-full h-full relative">
                <img
                  src={item.url}
                  alt={`${title} - ${current + 1}`}
                  className="w-full h-full"
                  style={{ objectFit: 'cover', objectPosition: 'center top' }}
                />
                {/* bottom gradient */}
                <div className="absolute inset-x-0 bottom-0 h-1/3 pointer-events-none"
                  style={{ background: 'linear-gradient(to top, oklch(0.12 0.025 280 / 0.7), transparent)' }} />
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        {/* Nav arrows – only when >1 item */}
        {sorted.length > 1 && (
          <>
            <button onClick={prev}
              className="absolute left-2 top-1/2 -translate-y-1/2 z-20 w-8 h-8 rounded-full flex items-center justify-center transition-all"
              style={{ background: 'oklch(0.10 0.022 280 / 0.75)', color: 'white', backdropFilter: 'blur(6px)' }}>
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button onClick={next}
              className="absolute right-2 top-1/2 -translate-y-1/2 z-20 w-8 h-8 rounded-full flex items-center justify-center transition-all"
              style={{ background: 'oklch(0.10 0.022 280 / 0.75)', color: 'white', backdropFilter: 'blur(6px)' }}>
              <ChevronRight className="w-4 h-4" />
            </button>
          </>
        )}

        {/* Dot indicators */}
        {sorted.length > 1 && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-20 flex gap-1.5">
            {sorted.map((_, i) => (
              <button key={i} onClick={() => setCurrent(i)}
                className="rounded-full transition-all duration-200"
                style={{
                  width: i === current ? '16px' : '6px',
                  height: '6px',
                  background: i === current ? accentColor : 'oklch(0.50 0.02 280 / 0.6)',
                }} />
            ))}
          </div>
        )}
      </div>

      {/* Thumbnails strip */}
      {sorted.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-1">
          {sorted.map((m, i) => (
            <button key={m.id} onClick={() => setCurrent(i)}
              className="flex-shrink-0 rounded-lg overflow-hidden border-2 transition-all duration-200"
              style={{
                width: '52px', height: '72px',  // ~9:16 thumbnails
                borderColor: i === current ? accentColor : 'oklch(0.25 0.045 280)',
                background: 'oklch(0.16 0.03 280)',
              }}>
              {m.type === 'model3d' ? (
                <div className="w-full h-full flex flex-col items-center justify-center gap-1"
                  style={{ background: `${accentColor}18` }}>
                  <Box className="w-4 h-4" style={{ color: accentColor }} />
                  <span className="text-xs font-bold" style={{ fontFamily: 'JetBrains Mono, monospace', color: accentColor, fontSize: '0.5rem' }}>3D</span>
                </div>
              ) : (
                <img src={m.url} alt={`thumb-${i}`} className="w-full h-full object-cover" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
